import React, { useCallback, useState, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { getData, API_ENDPOINTS } from "../../services/apiService";

const containerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '12px',
    backgroundColor: '#f5f5f5',
    position: 'relative'
};

const center = { lat: 20.5937, lng: 78.9629 };

const INDIA_BOUNDS = {
    north: 35.5133,
    south: 6.4627,
    west: 68.1097,
    east: 97.3954,
};

const mapStyles = [
    { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ visibility: "on" }, { color: "#444444" }] },
    { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] }
];

const LIBRARIES = ["places", "drawing"];

function LocationMapView() {
    const [view, setView] = useState('all');
    const [mapTilesLoaded, setMapTilesLoaded] = useState(false);
    const [loading, setLoading] = useState(false); // Now used in fetch
    const [cities, setCities] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyDxgYINpPW-E6u-OEJRh1-NY8-SCEYFW_s",
        libraries: LIBRARIES
    });

    const fetchCitiesAndBoardingPoints = useCallback(async () => {
        setLoading(true); // Start loading
        try {
            const response = await getData(API_ENDPOINTS.getCitiesData);
            if (response.status === "SUCCESS") {
                setCities(response.result.cities || []);
            }
        } catch (error) {
            console.error("Failed to fetch cities:", error);
        } finally {
            setLoading(false); // Stop loading
        }
    }, []);

    useEffect(() => {
        fetchCitiesAndBoardingPoints();
    }, [fetchCitiesAndBoardingPoints]);

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setView(nextView);
            setSelectedMarker(null);
        }
    };

    const options = useMemo(() => ({
        styles: mapStyles,
        restriction: {
            latLngBounds: INDIA_BOUNDS,
            strictBounds: true,
        },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        minZoom: 5,
        maxZoom: 12,
    }), []);

    const boardingPoints = useMemo(() => {
        return cities.flatMap(city => (city.BoardingDropPoints || []).map(bp => ({
            ...bp,
            parentCity: city.city
        })));
    }, [cities]);

    // Conditional rendering to handle the 'isLoaded' warning and script safety
    if (!isLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => window.history.back()} sx={{ color: 'text.primary' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>
                        India Service Map
                    </Typography>
                    {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
                </Stack>

                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleViewChange}
                    color="primary"
                    size="small"
                >
                    <ToggleButton value="all"><MapIcon fontSize="small" sx={{ mr: 1 }} /> All</ToggleButton>
                    <ToggleButton value="cities"><LocationCityIcon fontSize="small" sx={{ mr: 1 }} /> Cities</ToggleButton>
                    <ToggleButton value="boarding"><PlaceIcon fontSize="small" sx={{ mr: 1 }} /> Boarding Points</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={{ position: 'relative', width: '100%', height: '600px' }}>
                {!mapTilesLoaded && (
                    <Box sx={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.8)', borderRadius: '12px'
                    }}>
                        <CircularProgress size={60} thickness={4} />
                        <Typography sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>Loading Map Tiles...</Typography>
                    </Box>
                )}

                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={5}
                    options={options}
                    onTilesLoaded={() => setMapTilesLoaded(true)}
                >
                    {(view === 'all' || view === 'cities') && cities.map((city) => (
                        <Marker
                            key={`city-${city.id}`}
                            position={{ lat: parseFloat(city.latitude), lng: parseFloat(city.longitude) }}
                            // Standard Blue Marker
                            icon={"http://maps.google.com/mapfiles/ms/icons/blue-dot.png"}
                            onClick={() => setSelectedMarker({ ...city, type: 'City' })}
                        />
                    ))}

                    {(view === 'all' || view === 'boarding') && boardingPoints.map((bp) => (
                        <Marker
                            key={`bp-${bp.id}`}
                            position={{ lat: parseFloat(bp.latitude), lng: parseFloat(bp.longitude) }}
                            // Standard Green Marker
                            icon={"http://maps.google.com/mapfiles/ms/icons/green-dot.png"}
                            onClick={() => setSelectedMarker({ ...bp, type: 'Boarding Point' })}
                        />
                    ))}

                    {selectedMarker && (
                        <InfoWindow
                            position={{
                                lat: parseFloat(selectedMarker.latitude),
                                lng: parseFloat(selectedMarker.longitude)
                            }}
                            onCloseClick={() => setSelectedMarker(null)}
                        >
                            <Box sx={{ p: 1, maxWidth: 200 }}>
                                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                    {selectedMarker.type}
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {selectedMarker.city || selectedMarker.point}
                                </Typography>
                                {selectedMarker.address && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                        {selectedMarker.address}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    Status: {selectedMarker.status || 'Active'}
                                </Typography>
                            </Box>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </Box>
        </Box >
    );
}

export default React.memo(LocationMapView);